"use client";

import React, { createContext, useCallback, useState } from "react";

import { ActiveSearchContextState } from "types/common";

export interface SearchContextState {
  activeSearchContexts?: ActiveSearchContextState[];
  pushActiveSearchContext: (newContext: ActiveSearchContextState) => void;
  removeActiveSearchContext: (contextId: string) => void;
  resetActiveSearchContexts: (newContext?: ActiveSearchContextState) => void;
  updateHiddenSearchContextIds: (contextId?: string) => void;
  hiddenSearchContextIds?: string[];
  currentSearchContextRecordId: string | null;
  updateCurrentSearchContextRecordId: (recordId: string | null) => void;
  currentSearchContextProjectId: string | null;
  updateCurrentSearchContextProjectId: (projectId: string | null) => void;
}

export const SearchContext = createContext<SearchContextState | null>(null);

const { Provider } = SearchContext;

export const SearchContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [activeSearchContexts, setActiveSearchContexts] = useState<ActiveSearchContextState[]>([]);
  const [hiddenSearchContextIds, setHiddenSearchContextIds] = useState<string[]>([]);
  const [currentSearchContextRecordId, setCurrentSearchContextRecordId] = useState<string | null>(null); // This will replace currentRecordId in table context
  const [currentSearchContextProjectId, setCurrentSearchContextProjectId] = useState<string | null>(null); // This will replace currentDealId in table context
  const pushActiveSearchContext = useCallback((newContext: ActiveSearchContextState) => {
    setActiveSearchContexts((prev) => [...prev, newContext]);
  }, []);

  const removeActiveSearchContext = useCallback((contextId: string) => {
    setActiveSearchContexts((prev) => prev.filter((context) => context.id !== contextId));
  }, []);

  const resetActiveSearchContexts = useCallback((newContext?: ActiveSearchContextState) => {
    const finalContext = newContext ? [newContext] : [];
    setActiveSearchContexts(finalContext);
  }, []);

  const updateHiddenSearchContextIds = useCallback((contextId?: string) => {
    setHiddenSearchContextIds((prev) => (contextId ? [...prev, contextId] : []));
    // We also update the active search contexts to remove any non record id contexts
    if (!contextId) {
      setActiveSearchContexts((prev) => [prev?.[0]]);
    }
  }, []);

  const updateCurrentSearchContextRecordId = useCallback((recordId: string | null) => {
    setCurrentSearchContextRecordId(recordId);
  }, []);

  const updateCurrentSearchContextProjectId = useCallback((projectId: string | null) => {
    setCurrentSearchContextProjectId(projectId);
  }, []);

  return (
    <Provider
      value={{
        activeSearchContexts,
        pushActiveSearchContext,
        removeActiveSearchContext,
        resetActiveSearchContexts,
        updateHiddenSearchContextIds,
        hiddenSearchContextIds,
        currentSearchContextRecordId,
        updateCurrentSearchContextRecordId,
        currentSearchContextProjectId,
        updateCurrentSearchContextProjectId
      }}
    >
      {children}
    </Provider>
  );
};
